/**
 * Resolves the Calendly URL segment for routes without a showroom-detail override.
 *
 * Order (FSD Consultation Booking requirement 6; CF-008 / CF-011):
 * 1. Saved showroom from customer profile (registered, when loaded)
 * 2. Else My Showroom cookie (guest or fallback)
 * 3. Else nearest SFCC store with consultation booking + c_calendlyShowroomId
 *    (browser geolocation when available; on success, empty results may retry once with
 *    site defaultLocation). Geolocation errors (deny/timeout) do not use defaultLocation for
 *    this segment (KINDW-1296 — Find a showroom fallback off PDP).
 *
 * @module use-calendly-non-showroom-showroom-id
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useSearchStores, useStores} from '@salesforce/commerce-sdk-react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';

import {
  getMyShowroomFromCookie,
  parseAccountShowroom,
  SHOWROOM_CHANGED_EVENT,
} from '@amblique/king-living/app/utils/utils';
import {
  getSiteConfig,
  MAX_STORE_SEARCH_RADIUS,
} from '@amblique/king-living/app/constants/site-config';
import {isStoreConsultationBookingEnabled} from '@amblique/king-living/app/hooks/use-calendly-config';

const CLOSEST_STORE_SEARCH_LIMIT = 20;

/**
 * @param {boolean} enabled - When false, skips resolution (no network / geo).
 * @returns {{
 *   calendlyShowroomId: string | undefined,
 *   isResolutionComplete: boolean,
 * }}
 */
export const useCalendlyNonShowroomShowroomId = (enabled) => {
  const {site} = useMultiSite();
  const siteId = site?.id;
  const {data: customer, isLoading: isCustomerLoading} = useCurrentCustomer();
  const {isRegistered} = customer || {};

  const [showroomVersion, setShowroomVersion] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onShowroomChanged = () => setShowroomVersion((v) => v + 1);

    window.addEventListener(SHOWROOM_CHANGED_EVENT, onShowroomChanged);

    return () => {
      window.removeEventListener(SHOWROOM_CHANGED_EVENT, onShowroomChanged);
    };
  }, []);

  const siteConfig = useMemo(() => getSiteConfig(siteId), [siteId]);
  const defaultLocale = siteConfig?.defaultLocale || 'en-AU';
  const distanceUnit = siteConfig?.distanceUnit || 'km';

  const customerResolved = !isRegistered || !isCustomerLoading;

  const savedShowroom = useMemo(() => {
    if (!siteId || !customerResolved) return null;

    void showroomVersion;

    if (isRegistered && customer?.c_preferredStoreId) {
      const fromAccount = parseAccountShowroom(customer.c_preferredStoreId, siteId);

      if (fromAccount?.id) {
        return fromAccount;
      }
    }

    return getMyShowroomFromCookie(siteId);
  }, [
    siteId,
    customerResolved,
    isRegistered,
    customer?.c_preferredStoreId,
    showroomVersion
  ]);

  const savedStoreId = savedShowroom?.id;

  const {
    data: savedStoresData,
    isLoading: isSavedStoreLoading,
    isFetched: isSavedStoreFetched,
  } = useStores(
    {
      parameters: {
        ids: savedStoreId,
      },
    },
    {
      enabled: Boolean(enabled && savedStoreId),
    }
  );

  const savedStore = savedStoresData?.data?.[0];

  const calendlyFromSavedStore = useMemo(() => {
    if (!savedStore || !isStoreConsultationBookingEnabled(savedStore)) {
      return undefined;
    }

    const id = savedStore.c_calendlyShowroomId;

    return typeof id === 'string' && id.trim() !== '' ? id.trim() : undefined;
  }, [savedStore]);

  const savedPathComplete =
    !savedStoreId || (Boolean(isSavedStoreFetched) && !isSavedStoreLoading);

  const needClosest =
    Boolean(enabled) &&
    customerResolved &&
    savedPathComplete &&
    !calendlyFromSavedStore;

  const [searchCoords, setSearchCoords] = useState(null);
  const [closestCalendlyId, setClosestCalendlyId] = useState(undefined);
  const [closestSearchDone, setClosestSearchDone] = useState(false);
  const usedDefaultLocationFallbackRef = useRef(false);
  const geoRequestedRef = useRef(false);

  const resetClosestSearch = useCallback(() => {
    setSearchCoords(null);
    setClosestCalendlyId(undefined);
    setClosestSearchDone(false);
    usedDefaultLocationFallbackRef.current = false;
    geoRequestedRef.current = false;
  }, []);

  useEffect(() => {
    resetClosestSearch();
  }, [showroomVersion, siteId, savedStoreId, customer?.c_preferredStoreId, resetClosestSearch]);

  useEffect(() => {
    if (calendlyFromSavedStore) {
      resetClosestSearch();
    }
  }, [calendlyFromSavedStore, resetClosestSearch]);

  useEffect(() => {
    if (!needClosest || closestSearchDone || geoRequestedRef.current) {
      return;
    }

    const defaultLoc = siteConfig?.defaultLocation;

    geoRequestedRef.current = true;

    if (typeof window === 'undefined' || !navigator.geolocation) {
      if (defaultLoc?.lat != null && defaultLoc?.lng != null) {
        setSearchCoords({latitude: defaultLoc.lat, longitude: defaultLoc.lng});
      } else {
        setClosestSearchDone(true);
      }

      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setSearchCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        // KINDW-1296: Geolocation error (denied, timeout, unavailable). Do not fall back to
        // site defaultLocation for nearest-store Calendly resolution — that would still resolve
        // a segment and hide the "Find a showroom" CTA while the user did not share location.
        setClosestSearchDone(true);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
      }
    );
  }, [needClosest, closestSearchDone, siteConfig]);

  const {
    data: closestStoresData,
    isFetching: isClosestFetching,
    isError: isClosestError,
  } = useSearchStores(
    {
      parameters: {
        latitude: searchCoords?.latitude,
        longitude: searchCoords?.longitude,
        maxDistance: MAX_STORE_SEARCH_RADIUS,
        distanceUnit,
        limit: CLOSEST_STORE_SEARCH_LIMIT,
        locale: defaultLocale,
      },
    },
    {
      enabled: Boolean(
        needClosest &&
        !closestSearchDone &&
        searchCoords?.latitude != null &&
        searchCoords?.longitude != null
      ),
    }
  );

  useEffect(() => {
    if (!needClosest || closestSearchDone || !searchCoords) {
      return;
    }

    if (isClosestFetching) {
      return;
    }

    if (isClosestError) {
      setClosestSearchDone(true);

      return;
    }

    const list = closestStoresData?.data || [];
    const firstBookable = list.find((store) => {
      if (!isStoreConsultationBookingEnabled(store)) return false;
      const id = store.c_calendlyShowroomId;

      return typeof id === 'string' && id.trim() !== '';
    });

    if (firstBookable) {
      setClosestCalendlyId(String(firstBookable.c_calendlyShowroomId).trim());
      setClosestSearchDone(true);

      return;
    }

    if (closestStoresData && !isClosestFetching) {
      const defaultLoc = siteConfig?.defaultLocation;

      if (
        !usedDefaultLocationFallbackRef.current &&
        defaultLoc?.lat != null &&
        defaultLoc?.lng != null &&
        (searchCoords.latitude !== defaultLoc.lat ||
          searchCoords.longitude !== defaultLoc.lng)
      ) {
        usedDefaultLocationFallbackRef.current = true;
        setSearchCoords({latitude: defaultLoc.lat, longitude: defaultLoc.lng});

        return;
      }

      setClosestSearchDone(true);
    }
  }, [
    needClosest,
    closestSearchDone,
    searchCoords,
    isClosestFetching,
    isClosestError,
    closestStoresData,
    siteConfig
  ]);

  const isResolutionComplete = useMemo(() => {
    if (!enabled) {
      return true;
    }

    if (!customerResolved) {
      return false;
    }

    if (!savedPathComplete) {
      return false;
    }

    if (calendlyFromSavedStore) {
      return true;
    }

    return closestSearchDone;
  }, [enabled, customerResolved, savedPathComplete, calendlyFromSavedStore, closestSearchDone]);

  const calendlyShowroomId = useMemo(() => {
    if (!enabled) {
      return undefined;
    }

    return calendlyFromSavedStore ?? closestCalendlyId ?? undefined;
  }, [enabled, calendlyFromSavedStore, closestCalendlyId]);

  return useMemo(
    () => ({
      calendlyShowroomId,
      isResolutionComplete,
    }),
    [calendlyShowroomId, isResolutionComplete]
  );
};

export default useCalendlyNonShowroomShowroomId;
